import React, { useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import cn from 'classnames'
import { utils } from '@grammarly/badger-ui'

import { feMetrics } from '@lib/tracking/metrics'
import { Input } from '@components/ui-lib/form'
import { SessionStorageKey } from '@lib/utils/session-storage'
import * as CmsMetrics from '../../../lib/tracking/metrics/metrics'

import { toolTypeToMetricToolType } from '../types'
import { useTool } from '../tool-context'
import { analyzeTextAndMaybeTruncateToDesiredWordLimit } from '../utils'
import { maximumCharacterLimit, ToolErrors } from '../constants'
import { ToolFormValues } from '../types'

const { mergeRefs } = utils

import styles from '../tool.module.scss'

export const ToolInput: React.FC = () => {
  const { toolType, toolInputName, placeholder, setToolError, minWordLimit } = useTool()

  const { register, watch, clearErrors } = useFormContext<ToolFormValues>()
  const text = watch(toolInputName)

  const {
    ref: registerRef,
    onChange: registerOnChange,
    ...registerRest
  } = register(toolInputName, {
    validate: (value) => {
      const wordCount = analyzeTextAndMaybeTruncateToDesiredWordLimit(value).words

      if (wordCount < minWordLimit) {
        return ToolErrors.MIN_WORD_LIMIT_ERROR
      }

      if (wordCount === 0) {
        return ToolErrors.EMPTY_TEXT_ERROR
      }
    },
  })

  const inputRef = useRef<HTMLTextAreaElement>(null)
  const hasInitialChangeRef = useRef(false)

  const toolInputTrackingOnChange = () => {
    if (!hasInitialChangeRef.current) {
      feMetrics
        .getMetric(CmsMetrics.GeneralClientMetrics.ToolUser)
        .getCounter(CmsMetrics.ClientMetricsActions.Input)
        .setContext({
          toolPage: toolTypeToMetricToolType[toolType],
        })
        .increment()
      hasInitialChangeRef.current = true
    }
  }

  const mergeOnChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    registerOnChange(e)
    setToolError(null)
    clearErrors(toolInputName)
    toolInputTrackingOnChange()
  }

  // @todo Need to add standard onPaste tracking (https://grammarly.atlassian.net/browse/G2A-1967).

  useEffect(() => {
    if (sessionStorage.getItem(SessionStorageKey.editorContent) !== null) {
      sessionStorage.removeItem(SessionStorageKey.editorContent)
    }
  }, [])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (text) {
        sessionStorage.setItem(SessionStorageKey.editorContent, text)
      }
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [text])

  return (
    <div className={styles.toolTextInputContainer}>
      <Input
        as="textarea"
        ref={mergeRefs(registerRef, inputRef)}
        placeholder={placeholder}
        type="text"
        className={cn('font-family-inter', styles.textarea)}
        onChange={mergeOnChange}
        maxLength={maximumCharacterLimit(toolType)}
        autoFocus
        {...registerRest}
      />
    </div>
  )
}
